import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { 
  MagnifyingGlassIcon,
  UserGroupIcon,
  ClipboardDocumentListIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';

const CRMQueryNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const queryType = data.config?.queryType || 'customers_with_open_tasks';
  
  // Icon kiválasztása query típus alapján
  const getIcon = (type: string) => {
    const iconMap: Record<string, React.ComponentType<any>> = {
      customers_with_open_tasks: ClipboardDocumentListIcon,
      customers_by_status: UserGroupIcon,
      customers_by_filter: FunnelIcon,
    };
    return iconMap[type] || MagnifyingGlassIcon;
  };

  // Query típus címkéje
  const getQueryLabel = (type: string) => {
    const labelMap: Record<string, string> = {
      customers_with_open_tasks: 'Nyitott task-os ügyfelek',
      customers_by_status: 'Ügyfelek státusz szerint',
      customers_by_filter: 'Szűrt ügyfelek',
    };
    return labelMap[type] || type;
  };

  const Icon = getIcon(queryType);

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-purple-400 to-purple-500 border-2 transition-all duration-200 ${
      selected ? 'border-purple-600 shadow-xl scale-105' : 'border-purple-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-purple-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Query type */}
          <div className="text-xs text-purple-100 mt-1">
            {getQueryLabel(queryType)}
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Configuration preview */}
      {data.config && Object.keys(data.config).length > 1 && (
        <div className="mt-2 text-xs text-purple-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="space-y-1">
            {data.config.taskStatus && (
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-200 rounded-full" />
                <span className="truncate">
                  Task státusz: {data.config.taskStatus}
                </span>
              </div>
            )}
            {data.config.customerStatus && (
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-purple-200 rounded-full" />
                <span className="truncate">
                  Ügyfél státusz: {data.config.customerStatus}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Execution result */}
      {data.executionData && (
        <div className="mt-2 text-xs text-purple-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <div className="flex items-center space-x-2">
            <div className={`w-2 h-2 rounded-full ${
              data.executionData.success ? 'bg-green-400' : 'bg-red-400'
            }`} />
            <span>
              {data.executionData.customerCount || 0} ügyfél találva
            </span>
          </div>
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        id="trigger"
        className="w-3 h-3 bg-white border-2 border-purple-500"
        style={{ left: -6 }}
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="customers"
        className="w-3 h-3 bg-white border-2 border-purple-500"
        style={{ right: -6 }}
      />
    </div>
  );
};

export default CRMQueryNode;
